import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import listSalons from 'actions/listSalons.action';
import feResetCurrentSalon from 'actions/salon/feResetCurrentSalon.action';
import setCurrentSalon from 'actions/salon/setCurrentSalon.action';
import { fromSalonListObjectToSalonsInfo } from 'bl/salon/fromSalonListObjectToSalonsInfo.bl';
import { NoAccessDueToErrorPanel, NoAccessDueToListPanel } from 'components/messages/NoAccessPanel.component';
import USER_ROLE from 'constants/role';
import { useTranslation, withTranslation } from 'providers/GILT.provider';
import { selectSalonList } from 'selectors/salonInfo.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToUpperCase from 'utils/converters/any/fromAnyToUpperCase.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import determineFbSalonId from 'utils/determineFbSalonId.util';
import logger from 'utils/logger.util';
import createNodeKey from 'utils/react/createNodeKey.util';

const L = logger('Salon');

const compareNameOrIdWith = (searchText) => {
  const text = fromAnyToUpperCase(searchText);
  return (options) => '' === text || fromAnyToString(options?.id) === text || fromAnyToUpperCase(options?.name).indexOf(text) === 0;
};

const ManagerSalonButton = ({ id, name, onClick, isSupport }) => {
  const { t } = useTranslation();
  const trimmedId = fromAnyToTrimmed(id);
  const displayId = trimmedId || t('salon.unknownId');
  const playwrightId = `m:${trimmedId}`;

  return (
    <div data-bem="ManagerSalonButton" data-pw-id={playwrightId} className="list-grid-item" role="button" tabIndex={0} onClick={onClick}>
      <span>{name}</span>
      <div>({displayId})</div>
    </div>
  );
};

const ReceptionistSalonButton = ({ id, isSupport, name, onClick }) => {
  const { t } = useTranslation();
  const trimmedId = fromAnyToTrimmed(id);
  const displayId = trimmedId || t('salon.unknownId');
  const playwrightId = `r:${trimmedId}`;

  return (
    <div data-bem="ReceptionistSalonButton" data-pw-id={playwrightId} className="list-grid-item" role="button" tabIndex={0} onClick={onClick}>
      <div>
        <span>{name}</span>
        {fromAnyToBool(isSupport) && <div>({displayId})</div>}
      </div>
    </div>
  );
};

class Salon extends React.Component {
  static defaultProps = { salonId: null };

  constructor(props) {
    super(props);
    this.state = {
      searchText: '',
    };
  }

  UNSAFE_componentWillMount() {
    this.props.feResetCurrentSalon();
  }

  componentDidMount() {
    const { history, listSalons } = this.props;

    const salonId = determineFbSalonId();
    if (salonId) history.push(`/salon/${salonId}/onlinebooking`);

    L.unignore('componentDidMount()', 'listSalons:', listSalons());

    this.props.setCurrentSalon({ salonId: null, userRole: USER_ROLE.receptionist });
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { history } = this.props;
    const { salonList, salonId } = nextProps;

    if (salonId) history.push(`/salon/${salonId}/onlinebooking`);

    const { isSupport, manager, managerCount, receptionist, receptionistCount } = fromSalonListObjectToSalonsInfo(salonList);

    if (isSupport || nextProps?.current?.salon !== null) return;

    if (managerCount === 1 && receptionistCount === 0) {
      this.props.setCurrentSalon({ isSupport, salonId: manager?.[0]?.id, userRole: USER_ROLE.manager });
    }

    if (managerCount === 0 && receptionistCount === 1) {
      this.props.setCurrentSalon({ isSupport, salonId: receptionist?.[0]?.id, userRole: USER_ROLE.receptionist });
    }
  }

  handleSearch = (event) => void this.setState({ searchText: event.target.value });

  render() {
    const { searchText } = this.state;
    const { location, salonList, redirectUri, current, setCurrentSalon, isOk, t } = this.props;

    const bySalonNameOrId = compareNameOrIdWith(searchText);

    const isFetching = fromAnyToBool(salonList?.isFetching);
    const errorMessage = fromAnyToString(salonList?.errorMessage);
    const { isSupport, manager, receptionist, hasNoRole, hasRole } = fromSalonListObjectToSalonsInfo(salonList);

    if (!salonList) L.warn('render()', 'invalid salonList:', salonList);

    return (
      <div data-bem="Salon" className="salon-list">
        <div className="salon-wrap">
          <h3 data-bem="Salon__title" className="pink">
            {t('salon.title')}
          </h3>
          <input type="text" placeholder={t('salon.searchInputPlaceholder')} onChange={this.handleSearch} />

          <div className="detail-content-wrap search-content-wrap clearfix">
            {errorMessage && <div data-bem="Salon__error-message">{errorMessage}</div>}

            {isFetching ? (
              <div data-bem="Salon__spinner" className="spinner-container">
                <div className="load-spinner" />
              </div>
            ) : (
              hasNoRole && (isOk ? <NoAccessDueToListPanel /> : <NoAccessDueToErrorPanel />)
            )}

            {current?.salon !== null && (
              <Redirect
                to={{
                  pathname: `/salon/${current?.salon || 0}${redirectUri ? '/landing' : '/day/today'}`,
                  state: { from: location },
                }}
              />
            )}

            {hasRole && (
              <div className="flex">
                <div data-bem="Salon__manager" className="salon-suggestions">
                  <strong>{t('salon.manager')}</strong>

                  {manager.filter(bySalonNameOrId).map((item, index) => (
                    <ManagerSalonButton
                      key={createNodeKey({ tag: 'salon', unique: item.id, index })}
                      id={item.id}
                      onClick={() => setCurrentSalon({ isSupport, salonId: item.id, userRole: USER_ROLE.manager })}
                      name={item.name}
                      isSupport={isSupport}
                    />
                  ))}
                </div>

                <div data-bem="Salon__receptionist" className="salon-suggestions">
                  <strong>{t('salon.receptionist')}</strong>

                  {receptionist.filter(bySalonNameOrId).map((item) => (
                    <ReceptionistSalonButton
                      key={`settings-${item.id}`}
                      id={item.id}
                      onClick={() => setCurrentSalon({ isSupport, salonId: item.id, userRole: USER_ROLE.receptionist })}
                      name={item.name}
                      isSupport={isSupport}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const SalonHoc = connect(
  (state) => ({
    salonList: selectSalonList(state),
    current: state?.salon?.current,
    redirectUri: state?.salon?.userPermission?.redirectUri,
  }),
  mapActionsToDispatchToProps({
    listSalons,
    setCurrentSalon,
    feResetCurrentSalon,
  }),
)(withTranslation()(Salon));

export default SalonHoc;
